// Cave84 — Special Occasions Dashboard (Phase 5 supplement)
// Spec §4.4 F4.6 — dedicated screen, grouped view.
// Groups: Country / Color / Region / Vintage year.
// Each group accordion: total count + value, expandable wine rows with reason text.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useGroupedInventory, DEFAULT_FILTERS } from '@/hooks/useInventory';
import type { GroupedInventoryRow, DrinkingReadiness } from '@/hooks/useInventory';
import type { WineStyle } from '@cave84/shared-types';
import { Money } from '@/components/Money';

// ---------------------------------------------------------------------------
// Design tokens (mirrors DashboardPage / InventoryPage)
// ---------------------------------------------------------------------------
const C = {
  bgBase:       '#0E1116',
  surface1:     '#181D26',
  surface2:     '#1F1C19',
  surface3:     '#1B202B',
  borderSubtle: '#232936',
  borderMedium: '#2F3645',
  textPrimary:  '#ECE6D4',
  textSecondary:'#B3AC97',
  textTertiary: '#9C9483',
  textMuted:    '#908977',
  gold:         '#C49255',
  claret:       '#C49255',
  claretDeep:   '#8E663A',
  amber:        '#C49255',
  cyprus:       '#7E9A98',
  berlin:       '#8C95A8',
  romania:      '#8E663A',
  virtual:      '#9282A4',
};

// ---------------------------------------------------------------------------
// Static maps
// ---------------------------------------------------------------------------
const COUNTRY_NAME: Record<string, string> = {
  IL: 'Israel',   CY: 'Cyprus',  DE: 'Germany', RO: 'Romania',
  FR: 'France',   IT: 'Italy',   ES: 'Spain',   PT: 'Portugal',
  US: 'USA',      GB: 'UK',      AT: 'Austria', HU: 'Hungary',
  AU: 'Australia',AR: 'Argentina',NZ: 'New Zealand',
};
const COUNTRY_FLAG: Record<string, string> = {
  IL: '🇮🇱', CY: '🇨🇾', DE: '🇩🇪', RO: '🇷🇴', FR: '🇫🇷', IT: '🇮🇹',
  ES: '🇪🇸', PT: '🇵🇹', US: '🇺🇸', GB: '🇬🇧', AT: '🇦🇹', HU: '🇭🇺',
  AU: '🇦🇺', AR: '🇦🇷', NZ: '🇳🇿',
};

const STYLE_LABEL: Record<WineStyle, string> = {
  red:       'Red',
  white:     'White',
  rose:      'Rosé',
  sparkling: 'Sparkling',
  fortified: 'Fortified',
  sweet:     'Sweet',
  orange:    'Orange',
};
const STYLE_DOT: Record<WineStyle, string> = {
  red:       C.claret,
  white:     '#D4B96A',
  rose:      '#C9708A',
  sparkling: '#7ABECC',
  fortified: '#D4823A',
  sweet:     '#D4C44A',
  orange:    '#D4923A',
};

const STATUS_COLOR: Record<DrinkingReadiness, string> = {
  too_young:    C.cyprus,
  developing:   '#8FB57C',
  peak:         C.gold,
  declining:    '#C46F50',
  over_the_hill:'#908977',
};
const STATUS_LABEL: Record<DrinkingReadiness, string> = {
  too_young:    'Too Young',
  developing:   'Developing',
  peak:         'Peak',
  declining:    'Declining',
  over_the_hill:'Over the Hill',
};

type GroupMode = 'country' | 'color' | 'region' | 'vintage';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function groupKey(row: GroupedInventoryRow, mode: GroupMode): string {
  switch (mode) {
    case 'country': return row.cellar.country || 'XX';
    case 'color':   return row.wine.style;
    case 'region':  return row.region || 'Other';
    case 'vintage': return String(row.vintage.year);
  }
}

function groupLabel(key: string, mode: GroupMode): string {
  switch (mode) {
    case 'country': return (COUNTRY_FLAG[key] ?? '🌍') + ' ' + (COUNTRY_NAME[key] ?? key);
    case 'color':   return STYLE_LABEL[key as WineStyle] ?? key;
    case 'region':  return key;
    case 'vintage': return key;
  }
}

function groupSort(a: string, b: string, mode: GroupMode): number {
  if (mode === 'vintage') return Number(b) - Number(a); // newest first
  return a.localeCompare(b);
}

/** Collect unique, non-empty occasion reasons from all bottles in a group row. */
function reasonsFor(row: GroupedInventoryRow): string[] {
  const seen = new Set<string>();
  for (const bottle of row.bottles) {
    const r = bottle.special_occasion_reason?.trim();
    if (r) seen.add(r);
  }
  return [...seen];
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
interface WineRowProps { row: GroupedInventoryRow; mode: GroupMode }

function WineRow({ row, mode }: WineRowProps) {
  const reasons = reasonsFor(row);
  const value   = row.qty * (row.marketPrice ?? 0);

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      gap: '4px 12px',
      padding: '10px 16px',
      borderBottom: `1px solid ${C.borderSubtle}`,
    }}>
      {/* Left: wine info */}
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {/* Style dot (only when not grouped by color) */}
          {mode !== 'color' && (
            <span style={{
              width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
              background: STYLE_DOT[row.wine.style],
            }} />
          )}
          <Link
            to={`/wines/${row.wine.id}?vintage=${row.vintage.year}`}
            style={{ color: C.textPrimary, fontWeight: 500, fontSize: 13, textDecoration: 'none' }}
          >
            {row.producer.name} — {row.wine.cuvee_name || row.appellation?.name || 'Unknown wine'}
          </Link>
          <span style={{ color: C.textTertiary, fontSize: 12 }}>{row.vintage.year}</span>
          <span style={{ color: C.textMuted, fontSize: 11 }}>{row.format}</span>
        </div>

        {/* Reasons */}
        {reasons.length > 0 && (
          <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {reasons.map(r => (
              <span key={r} style={{
                background: '#2A2010',
                border: `1px solid #4A3A10`,
                color: C.gold,
                fontSize: 11,
                padding: '1px 7px',
                borderRadius: 10,
              }}>★ {r}</span>
            ))}
          </div>
        )}

        {/* Meta row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
          {/* Drinking status */}
          <span style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.05em',
            color: STATUS_COLOR[row.drinkingStatus],
            textTransform: 'uppercase',
          }}>
            {STATUS_LABEL[row.drinkingStatus]}
          </span>
          <span style={{ color: C.borderMedium }}>·</span>
          {/* Cellar (only when not grouped by country) */}
          {mode !== 'country' && (
            <>
              <span style={{ fontSize: 11, color: C.textMuted }}>
                {(COUNTRY_FLAG[row.cellar.country] ?? '📍')} {row.cellar.name}
              </span>
              <span style={{ color: C.borderMedium }}>·</span>
            </>
          )}
          {/* Region (only when not grouped by region) */}
          {mode !== 'region' && row.region && (
            <span style={{ fontSize: 11, color: C.textMuted }}>{row.region}</span>
          )}
        </div>
      </div>

      {/* Right: qty + value */}
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: C.textPrimary }}>
          {row.qty}×
        </div>
        {value > 0 && (
          <div style={{ fontSize: 11, color: C.textTertiary, marginTop: 2 }}><Money value={value} currency="EUR" minWidthCh={6} /></div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Group accordion
// ---------------------------------------------------------------------------
interface GroupCardProps {
  groupKey: string;
  mode: GroupMode;
  rows: GroupedInventoryRow[];
}

function GroupCard({ groupKey: gk, mode, rows }: GroupCardProps) {
  const [open, setOpen] = useState(true);

  const totalQty = rows.reduce((s, r) => s + r.qty, 0);
  const totalVal = rows.reduce((s, r) => s + r.qty * (r.marketPrice ?? 0), 0);

  return (
    <div style={{
      background: C.surface1,
      border: `1px solid ${C.borderMedium}`,
      borderRadius: 10,
      overflow: 'hidden',
      marginBottom: 8,
    }}>
      {/* Header */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          {/* Color dot for color mode */}
          {mode === 'color' && (
            <span style={{
              width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
              background: STYLE_DOT[gk as WineStyle] ?? C.textMuted,
            }} />
          )}
          <span style={{ fontSize: 14, fontWeight: 600, color: C.textPrimary }}>
            {groupLabel(gk, mode)}
          </span>
          <span style={{
            fontSize: 11,
            fontWeight: 700,
            color: C.gold,
            background: '#2A2010',
            border: `1px solid #4A3A10`,
            borderRadius: 10,
            padding: '1px 8px',
          }}>
            ★ {totalQty} {totalQty === 1 ? 'bottle' : 'bottles'}
          </span>
          {totalVal > 0 && (
            <span style={{ fontSize: 12, color: C.textTertiary }}><Money value={totalVal} currency="EUR" minWidthCh={6} /></span>
          )}
        </div>
        {/* Chevron */}
        <span style={{
          color: C.textMuted,
          fontSize: 12,
          transform: open ? 'rotate(180deg)' : 'rotate(0)',
          transition: 'transform 0.2s',
          flexShrink: 0,
        }}>▼</span>
      </button>

      {/* Rows */}
      {open && (
        <div style={{ borderTop: `1px solid ${C.borderSubtle}` }}>
          {rows
            .sort((a, b) => (b.marketPrice ?? 0) * b.qty - (a.marketPrice ?? 0) * a.qty)
            .map(row => (
              <WineRow key={row.key} row={row} mode={mode} />
            ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const SPECIAL_FILTER = { ...DEFAULT_FILTERS, availableAtHome: false, specialOccasion: true };

const GROUP_MODES: { id: GroupMode; label: string }[] = [
  { id: 'country', label: 'Country' },
  { id: 'color',   label: 'Color' },
  { id: 'region',  label: 'Region' },
  { id: 'vintage', label: 'Vintage' },
];

export default function SpecialOccasionsPage() {
  const [mode, setMode] = useState<GroupMode>('country');
  const { grouped, isLoading, facets } = useGroupedInventory(SPECIAL_FILTER);

  // Build groups
  const groups = useMemo<[string, GroupedInventoryRow[]][]>(() => {
    const map = new Map<string, GroupedInventoryRow[]>();
    for (const row of grouped) {
      const k = groupKey(row, mode);
      const arr = map.get(k) ?? [];
      arr.push(row);
      map.set(k, arr);
    }
    return [...map.entries()].sort(([a], [b]) => groupSort(a, b, mode));
  }, [grouped, mode]);

  const totalBottles = facets.specialOccasionCount;
  const totalValue   = useMemo(
    () => grouped.reduce((s, r) => s + r.qty * (r.marketPrice ?? 0), 0),
    [grouped]
  );
  const withValue = useMemo(
    () => grouped.filter(r => r.marketPrice && r.marketPrice > 0).reduce((s, r) => s + r.qty, 0),
    [grouped]
  );

  return (
    <div style={{ maxWidth: 780, margin: '0 auto', padding: '24px 16px 40px' }}>

      {/* ── Header ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span style={{ fontSize: 20, color: C.gold }}>★</span>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.textPrimary, margin: 0 }}>
            Special Occasions
          </h1>
        </div>
        <p style={{ fontSize: 13, color: C.textTertiary, margin: 0 }}>
          Bottles set aside for specific occasions, milestones, or memories.
        </p>
      </div>

      {/* ── Stats bar ── */}
      {!isLoading && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 8,
          marginBottom: 20,
        }}>
          {[
            { label: 'Total bottles', value: totalBottles > 0 ? String(totalBottles) : '—' },
            { label: 'Total value',   value: totalValue > 0 ? <Money value={totalValue} currency="EUR" minWidthCh={7} /> : '—' },
            { label: 'Groups',        value: groups.length > 0 ? String(groups.length) : '—' },
          ].map(({ label, value }) => (
            <div key={label} style={{
              background: C.surface1,
              border: `1px solid ${C.borderMedium}`,
              borderRadius: 8,
              padding: '12px 14px',
            }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: C.gold }}>{value}</div>
              <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Group mode selector ── */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        <span style={{ fontSize: 12, color: C.textMuted, alignSelf: 'center', marginRight: 4 }}>
          Group by
        </span>
        {GROUP_MODES.map(m => (
          <button
            key={m.id}
            type="button"
            onClick={() => setMode(m.id)}
            style={{
              padding: '5px 12px',
              borderRadius: 20,
              fontSize: 12,
              fontWeight: mode === m.id ? 600 : 400,
              background: mode === m.id ? C.claretDeep : C.surface2,
              border: `1px solid ${mode === m.id ? C.claret : C.borderMedium}`,
              color: mode === m.id ? '#F5CDD5' : C.textSecondary,
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* ── Content ── */}
      {isLoading ? (
        <div style={{ color: C.textMuted, fontSize: 13, padding: '40px 0', textAlign: 'center' }}>
          Loading…
        </div>
      ) : groups.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '60px 24px',
          color: C.textMuted, fontSize: 13,
        }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>★</div>
          <div style={{ marginBottom: 6 }}>No special occasion bottles yet.</div>
          <div>
            Mark a bottle as special from the{' '}
            <Link to="/inventory" style={{ color: C.gold }}>Inventory</Link>{' '}
            or{' '}
            <Link to="/inventory" style={{ color: C.gold }}>Bottle Detail</Link> page.
          </div>
        </div>
      ) : (
        <div>
          {groups.map(([gk, rows]) => (
            <GroupCard key={gk} groupKey={gk} mode={mode} rows={rows} />
          ))}
        </div>
      )}

      {/* ── Value disclaimer ── */}
      {!isLoading && withValue > 0 && withValue < totalBottles && (
        <p style={{ fontSize: 11, color: C.textMuted, marginTop: 12, textAlign: 'center' }}>
          Value shown only for bottles with a current market price ({withValue}/{totalBottles} bottles have pricing).
        </p>
      )}
    </div>
  );
}
